import React, { useState, Suspense, lazy } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/UI/PageTransition';
import { FaEnvelope, FaLock, FaFutbol, FaEye, FaEyeSlash, FaDownload, FaCamera, FaFingerprint } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Button from '../components/UI/Button';
import { useAuth } from '../context/AuthContext';
import usePWA from '../hooks/usePWA';
import api from '../api/axios';

// Chargé UNIQUEMENT quand le joueur demande la reconnaissance faciale : la
// bibliothèque et ses modèles pèsent environ 7 Mo, ce qui serait inacceptable
// à imposer à chaque ouverture de l'application en données mobiles.
const BiometricLogin = lazy(() => import('../components/BiometricLogin'));

// WebAuthn : quelques kilo-octets seulement, on peut l'importer normalement
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';

function Login() {
  const navigate = useNavigate();
  const { login, loginWithToken } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isInstallable, isInstalled, promptInstall } = usePWA();
  const [faceMode, setFaceMode] = useState(false);
  const [empreinteEnCours, setEmpreinteEnCours] = useState(false);
  const showInstallCard = isInstallable && !isInstalled;

  // Connexion par reconnaissance faciale réussie
  const handleFaceSuccess = (bioUser, token) => {
    if (!token) { toast.error('Connexion incomplète, réessaie'); return; }
    const u = loginWithToken(token, bioUser);
    toast.success('Connexion par reconnaissance faciale réussie !');
    navigate(u?.role === 'admin' ? '/admin' : '/');
  };

  // Connexion par empreinte digitale du téléphone
  const connexionEmpreinte = async () => {
    if (!form.email) {
      toast.error('Saisis ton email pour utiliser ton empreinte');
      return;
    }
    setEmpreinteEnCours(true);
    try {
      const { data } = await api.post('/webauthn/login/options', { email: form.email });
      const reponse = await startAuthentication({ optionsJSON: data.options });
      const { data: res } = await api.post('/webauthn/login/verify', { email: form.email, response: reponse });
      const u = loginWithToken(res.token, res.user);
      toast.success('Connexion par empreinte réussie !');
      navigate(u?.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      if (err?.name === 'NotAllowedError') toast.error('Connexion annulée');
      else toast.error(err?.response?.data?.message || 'Connexion par empreinte impossible');
    } finally {
      setEmpreinteEnCours(false);
    }
  };

  const ouvrirReconnaissance = () => {
    if (!form.email) {
      toast.error("Saisis ton email avant d'utiliser la reconnaissance faciale");
      return;
    }
    setFaceMode(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Remplis tous les champs');
      return;
    }

    setLoading(true);
    try {
      const loggedUser = await login(form.email, form.password);
      toast.success('Connexion réussie !');
      // Admin → interface d'administration, joueur → accueil
      navigate(loggedUser?.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Email ou mot de passe incorrect';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition variant="zoomIn">
    <div
      className="app-container flex flex-col min-h-screen"
      style={{
        background: 'radial-gradient(ellipse at top, #1a4a2e 0%, #0d2b1a 70%)',
      }}
    >
      {/* Header décoratif */}
      <div
        className="flex-shrink-0 h-48 flex flex-col items-center justify-end pb-6"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(240,180,41,0.15) 0%, transparent 70%)',
        }}
      >
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="w-20 h-20 rounded-full bg-qfl-card border-2 border-qfl-gold flex items-center justify-center mb-3"
          style={{ boxShadow: '0 0 30px rgba(240,180,41,0.3)' }}
        >
          <FaFutbol size={36} className="text-qfl-gold" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl font-black uppercase tracking-widest"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            color: '#f0b429',
            textShadow: '0 0 20px rgba(240,180,41,0.4)',
          }}
        >
          QuizFoot League
        </motion.h1>
      </div>

      {/* Formulaire */}
      <motion.div
        className="flex-1 flex flex-col justify-center px-6 py-8"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
      >
        {/* Bannière d'installation PWA */}
        {showInstallCard && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={promptInstall}
            className="flex items-center gap-3 rounded-2xl p-3 mb-4 cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, rgba(240,180,41,0.12), rgba(240,180,41,0.06))',
              border: '1px solid rgba(240,180,41,0.35)',
            }}
          >
            <img src="/logo-qfl-clear.png" alt="QFL" style={{ width: 36, height: 36, objectFit: 'contain', mixBlendMode: 'screen', flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm m-0" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Installer QuizFoot League
              </p>
              <p className="text-white/50 text-xs m-0">Joue comme une vraie app, sans navigateur</p>
            </div>
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #f0b429, #d4920a)', color: '#0d2b1a', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '0.75rem' }}
            >
              <FaDownload size={11} />
              INSTALLER
            </div>
          </motion.div>
        )}

        <h2
          className="text-xl font-bold text-white mb-6 text-center"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          CONNEXION
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Email */}
          <div className="relative">
            <FaEnvelope
              size={14}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="form-input pl-11"
              autoComplete="email"
            />
          </div>

          {/* Mot de passe */}
          <div className="relative">
            <FaLock
              size={14}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Mot de passe"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="form-input pl-11 pr-11"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
            >
              {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
            </button>
          </div>

          {/* Bouton connexion */}
          <Button
            type="submit"
            variant="gold"
            size="lg"
            fullWidth
            loading={loading}
            className="mt-2"
          >
            CONNEXION
          </Button>

          <p className="text-center mt-2">
            <Link
              to="/forgot-password"
              className="text-white/40 text-xs hover:text-white/70 transition-colors"
            >
              Mot de passe oublié ?
            </Link>
          </p>
        </form>

        {/* Reconnaissance faciale — à la demande uniquement */}
        {!faceMode ? (
          <div className="mt-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/30 text-[11px] uppercase tracking-widest">ou</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            {browserSupportsWebAuthn() && (
              <button
                type="button"
                onClick={connexionEmpreinte}
                disabled={empreinteEnCours}
                className="w-full py-3 rounded-xl font-bold text-sm text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-2 mb-2 disabled:opacity-50"
              >
                <FaFingerprint size={15} />
                {empreinteEnCours ? 'Suis les instructions de ton téléphone…' : 'Connexion par empreinte digitale'}
              </button>
            )}

            <button
              type="button"
              onClick={ouvrirReconnaissance}
              className="w-full py-3 rounded-xl font-bold text-sm text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
            >
              <FaCamera size={14} /> Connexion par reconnaissance faciale
            </button>
            <p className="text-white/25 text-[11px] text-center mt-2 leading-relaxed">
              Nécessite un enregistrement préalable depuis ton profil.
              Environ 7 Mo seront téléchargés à la première utilisation.
            </p>
          </div>
        ) : (
          <div className="mt-5">
            <Suspense fallback={
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-white/50 text-xs">Chargement de la reconnaissance faciale…</p>
              </div>
            }>
              <BiometricLogin
                email={form.email}
                mode="login"
                onLoginSuccess={handleFaceSuccess}
              />
            </Suspense>
            <button
              type="button"
              onClick={() => setFaceMode(false)}
              className="w-full mt-3 py-2 text-white/40 text-xs hover:text-white/70"
            >
              ← Revenir au mot de passe
            </button>
          </div>
        )}

        {/* Lien inscription */}
        <p className="text-center text-white/50 text-sm mt-6">
          Pas encore de compte ?{' '}
          <Link
            to="/register"
            className="text-qfl-gold font-bold hover:text-yellow-400 transition-colors"
          >
            S'inscrire
          </Link>
        </p>

        <p className="text-center text-white/25 text-xs mt-6">
          <a
            href="https://quizfootleague.dekanisoftware.com/politique-confidentialite.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white/50 transition-colors"
          >
            Politique de confidentialité
          </a>
          {' · '}© 2026 DEKANI SOFTWARE
        </p>
      </motion.div>
    </div>
    </PageTransition>
  );
}

export default Login;
